import { FIELD_META, SECTION_OF_FIELD } from "@/features/profile/fieldMeta";
import { fieldLabel } from "@/features/profile/labels";
import { STEP_BY_FIELD } from "@/features/profile/wizard/steps";

/**
 * HR's ticks and crosses on a sign-up.
 *
 * HR marks each field right or wrong while checking it. A cross carries a
 * note, and is what the employee sees beside the field when the profile comes
 * back to them. A cross the employee has since changed stays a cross, marked
 * as changed, until HR looks again and ticks it.
 */
export type ReviewVerdict = "ok" | "issue";

export interface ReviewFlag {
  verdict: ReviewVerdict;
  note: string | null;
  /** Who marked it, and when. */
  by: string;
  at: string;
  /** The value it was marked against. */
  value: unknown;
  /** The employee has changed a crossed field since it was crossed. */
  changed_after?: boolean;
  /** When the employee last changed it, while the profile was with them. */
  changed_at?: string;
}

export type ReviewFlags = Record<string, ReviewFlag>;

/** What the review screen sends: a verdict and a note, or null to take it off. */
export type ReviewMark = { verdict: ReviewVerdict | null; note?: string | null } | null;

export const NOTE_LIMIT = 300;

export interface ReviewCounts {
  /** Ticked. */
  checked: number;
  /** Crossed and not yet changed by the employee. */
  toFix: number;
  /** Crossed, then changed by the employee: to be looked at again. */
  changed: number;
  /** Fields with any mark. */
  marked: number;
  /** Fields on the screen. */
  total: number;
}

/**
 * A cross on a field the employee cannot change, made before the server
 * refused them: their days off once the rota answers for them, or something
 * only HR sets. It asks nothing of anybody, so it is counted nowhere.
 */
export function isDeadCross(
  flag: ReviewFlag | undefined,
  field: string,
  hrSets: readonly string[] | undefined,
): boolean {
  return flag?.verdict === "issue" && Boolean(hrSets?.includes(field));
}

/** How far through the fields HR is, counting only the fields on screen. */
export function reviewCounts(
  flags: ReviewFlags | undefined,
  keys: readonly string[],
  hrSets?: readonly string[],
): ReviewCounts {
  let checked = 0;
  let toFix = 0;
  let changed = 0;
  for (const key of keys) {
    const flag = flags?.[key];
    if (!flag || isDeadCross(flag, key, hrSets)) continue;
    if (flag.verdict === "ok") checked += 1;
    else if (flag.changed_after) changed += 1;
    else toFix += 1;
  }
  return { checked, toFix, changed, marked: checked + toFix + changed, total: keys.length };
}

/** "12 checked, 2 to fix", as the review screen says it. */
export function countsText(counts: ReviewCounts): string {
  const parts = [`${counts.checked} checked`];
  if (counts.toFix) parts.push(`${counts.toFix} to fix`);
  if (counts.changed) parts.push(`${counts.changed} changed, to check again`);
  return parts.join(", ");
}

export interface CrossedField {
  field: string;
  label: string;
  note: string | null;
  /** The sign-up step to go to, to put it right. */
  stepKey: string;
  changed: boolean;
}

/**
 * The crosses, in the order of the sign-up form, less any on a field the
 * employee cannot change (see isDeadCross).
 */
export function crossedFields(
  flags: ReviewFlags | undefined,
  hrSets?: readonly string[],
): CrossedField[] {
  if (!flags) return [];
  const order = Object.keys(FIELD_META);
  return Object.entries(flags)
    .filter(([field, flag]) => flag?.verdict === "issue" && !isDeadCross(flag, field, hrSets))
    .sort(([a], [b]) => rank(order, a) - rank(order, b))
    .map(([field, flag]) => ({
      field,
      label: fieldLabel(field),
      note: flag.note,
      stepKey: STEP_BY_FIELD[field] ?? SECTION_OF_FIELD[field] ?? "review",
      changed: Boolean(flag.changed_after),
    }));
}

function rank(order: string[], key: string): number {
  const index = order.indexOf(key);
  return index === -1 ? order.length : index;
}

/**
 * HR's note for a field the employee has to put right, to show under it.
 *
 * The days off are one answer in two columns, so a cross on either shows on
 * the question.
 */
export function hrNote(
  flags: ReviewFlags | undefined,
  field: string,
  hrSets?: readonly string[],
): string | undefined {
  const key = flags?.[field] ? field : field === "days_off_rotate" ? "weekly_off_days" : field;
  const flag = flags?.[key];
  if (!flag || flag.verdict !== "issue" || isDeadCross(flag, key, hrSets)) return undefined;
  return `HR: ${flag.note ?? "Check this."}`;
}

/** The crosses on one sign-up step. */
export function crossedOnStep(
  flags: ReviewFlags | undefined,
  stepKey: string,
  hrSets?: readonly string[],
): CrossedField[] {
  return crossedFields(flags, hrSets).filter((crossed) => crossed.stepKey === stepKey);
}
